import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './PackageCustomization.css';

interface ProcessStep {
  id: string;
  number: number;
  title: string;
  description: string;
}

interface Expectation {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  id: string;
  content: string;
  author: string;
  service: string;
}

interface CustomizationData {
  processSteps: ProcessStep[];
  expectations: Expectation[];
  testimonials: Testimonial[];
  globalBookingNote: string;
}

interface ImportDialogState {
  isOpen: boolean;
  section: 'processSteps' | 'expectations' | 'testimonials' | null;
  data: any[];
  onConfirm: (replace: boolean) => void;
}

const PackageCustomization: React.FC = () => {
  const [customization, setCustomization] = useState<CustomizationData>({
    processSteps: [],
    expectations: [],
    testimonials: [],
    globalBookingNote: 'Free consultation included • Flexible scheduling • Professional results guaranteed'
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [importDialog, setImportDialog] = useState<ImportDialogState>({
    isOpen: false,
    section: null,
    data: [],
    onConfirm: () => {}
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    loadCustomization();
  }, []);

  const loadCustomization = async () => {
    try {
      // Try server first
      const res = await fetch('/api/service-customization');
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setCustomization(data);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch from server, using localStorage');
    }

    // Fallback to localStorage
    try {
      const stored = localStorage.getItem('package_customization') || localStorage.getItem('service_customization');
      if (stored) {
        const data = JSON.parse(stored);
        setCustomization(prev => ({ ...prev, ...data }));
      } else {
        // Initialize with default data
        const defaultData: CustomizationData = {
          processSteps: [
            { id: '1', number: 1, title: 'Initial Consultation', description: 'I\'ll discuss your vision, preferences, and specific requirements to tailor the perfect session for you.' },
            { id: '2', number: 2, title: 'Planning & Preparation', description: 'I\'ll provide guidance on wardrobe, location, timing, and any special considerations for your session.' },
            { id: '3', number: 3, title: 'Photography Session', description: 'Professional photography with natural posing guidance and a relaxed, enjoyable atmosphere.' },
            { id: '4', number: 4, title: 'Editing & Delivery', description: 'Careful editing of your images with professional retouching and delivery via online gallery.' }
          ],
          expectations: [
            { id: '1', icon: 'fas fa-camera', title: 'Professional Equipment', description: 'State-of-the-art camera gear and professional lighting equipment for stunning results.' },
            { id: '2', icon: 'fas fa-heart', title: 'Personal Touch', description: 'Every session is tailored to your unique style and personality for authentic, meaningful photos.' },
            { id: '3', icon: 'fas fa-clock', title: 'Flexible Timing', description: 'I work around your schedule to find the perfect time and lighting for your session.' },
            { id: '4', icon: 'fas fa-star', title: 'Quality Guarantee', description: 'Professional editing and retouching to ensure you receive only the highest quality images.' }
          ],
          testimonials: [
            { id: '1', content: 'Carmen has an incredible eye for detail and made our session so comfortable and fun. The photos exceeded our expectations!', author: 'Sarah M.', service: 'Portrait Package' },
            { id: '2', content: 'Professional, creative, and so easy to work with. Carmen captured our family perfectly and we\'ll treasure these photos forever.', author: 'John & Lisa', service: 'Family Package' },
            { id: '3', content: 'The wedding photos are absolutely stunning! Carmen captured every special moment beautifully. Highly recommended!', author: 'Michael & Emma', service: 'Wedding Package' }
          ],
          globalBookingNote: 'Free consultation included • Flexible scheduling • Professional results guaranteed'
        };
        setCustomization(defaultData);
        saveCustomization(defaultData);
      }
    } catch (error) {
      console.error('Error loading customization:', error);
    }
  };

  const saveCustomization = async (data: CustomizationData, showStatus = true) => {
    if (showStatus) {
      setSaving(true);
      setSaveStatus('saving');
    }
    try {
      // Try server first
      const res = await fetch('/api/service-customization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        console.log('Customization saved to server');
      }
    } catch (err) {
      console.warn('Server save failed, using localStorage');
    }

    // Always save to localStorage as backup
    localStorage.setItem('package_customization', JSON.stringify(data));
    localStorage.setItem('service_customization', JSON.stringify(data));
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('service_customization_updated', { detail: data }));
    
    if (showStatus) {
      setSaving(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  // Auto-save on changes
  const updateCustomization = (updater: (prev: CustomizationData) => CustomizationData) => {
    setCustomization(prev => {
      const updated = updater(prev);
      
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Auto-save after 1 second of no changes
      saveTimeoutRef.current = setTimeout(() => {
        saveCustomization(updated, false);
      }, 1000);
      
      return updated;
    });
  };

  const addProcessStep = () => {
    const newStep: ProcessStep = {
      id: `step-${Date.now()}`,
      number: customization.processSteps.length + 1,
      title: '',
      description: ''
    };
    updateCustomization(prev => ({
      ...prev,
      processSteps: [...prev.processSteps, newStep]
    }));
  };

  const updateProcessStepOrder = (id: string, newNumber: number) => {
    const steps = [...customization.processSteps];
    const currentIndex = steps.findIndex(step => step.id === id);
    if (currentIndex === -1) return;

    // Clamp the number to valid range
    const clampedNumber = Math.max(1, Math.min(newNumber, steps.length));
    
    // Calculate new index (0-based, so subtract 1)
    const newIndex = clampedNumber - 1;

    // Remove item from current position
    const [movedStep] = steps.splice(currentIndex, 1);
    
    // Insert at new position
    steps.splice(newIndex, 0, movedStep);
    
    // Update all numbers based on new order
    steps.forEach((step, i) => {
      step.number = i + 1;
    });

    updateCustomization(prev => ({
      ...prev,
      processSteps: steps
    }));
  };

  const updateProcessStep = (id: string, field: keyof ProcessStep, value: string | number) => {
    updateCustomization(prev => ({
      ...prev,
      processSteps: prev.processSteps.map(step => 
        step.id === id ? { ...step, [field]: value } : step
      )
    }));
  };

  const removeProcessStep = (id: string) => {
    updateCustomization(prev => ({
      ...prev,
      processSteps: prev.processSteps.filter(step => step.id !== id)
        .map((step, index) => ({ ...step, number: index + 1 }))
    }));
  };

  const addExpectation = () => {
    const newExpectation: Expectation = {
      id: `exp-${Date.now()}`,
      icon: 'fas fa-star',
      title: '',
      description: ''
    };
    updateCustomization(prev => ({
      ...prev,
      expectations: [...prev.expectations, newExpectation]
    }));
  };

  const updateExpectation = (id: string, field: keyof Expectation, value: string) => {
    updateCustomization(prev => ({
      ...prev,
      expectations: prev.expectations.map(exp => 
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeExpectation = (id: string) => {
    updateCustomization(prev => ({
      ...prev,
      expectations: prev.expectations.filter(exp => exp.id !== id)
    }));
  };

  const updateExpectationOrder = (id: string, newOrder: number) => {
    const expectations = [...customization.expectations];
    const currentIndex = expectations.findIndex(exp => exp.id === id);
    if (currentIndex === -1) return;

    // Clamp the order to valid range
    const clampedOrder = Math.max(1, Math.min(newOrder, expectations.length));
    
    // Calculate new index (0-based, so subtract 1)
    const newIndex = clampedOrder - 1;

    // Remove item from current position
    const [movedExpectation] = expectations.splice(currentIndex, 1);
    
    // Insert at new position
    expectations.splice(newIndex, 0, movedExpectation);

    updateCustomization(prev => ({
      ...prev,
      expectations: expectations
    }));
  };

  const addTestimonial = () => {
    const newTestimonial: Testimonial = {
      id: `test-${Date.now()}`,
      content: '',
      author: '',
      service: ''
    };
    updateCustomization(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, newTestimonial]
    }));
  };

  const updateTestimonial = (id: string, field: keyof Testimonial, value: string) => {
    updateCustomization(prev => ({
      ...prev,
      testimonials: prev.testimonials.map(test => 
        test.id === id ? { ...test, [field]: value } : test
      )
    }));
  };

  const removeTestimonial = (id: string) => {
    updateCustomization(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter(test => test.id !== id)
    }));
  };

  const updateTestimonialOrder = (id: string, newOrder: number) => {
    const testimonials = [...customization.testimonials];
    const currentIndex = testimonials.findIndex(test => test.id === id);
    if (currentIndex === -1) return;

    // Clamp the order to valid range
    const clampedOrder = Math.max(1, Math.min(newOrder, testimonials.length));
    
    // Calculate new index (0-based, so subtract 1)
    const newIndex = clampedOrder - 1;

    // Remove item from current position
    const [movedTestimonial] = testimonials.splice(currentIndex, 1);
    
    // Insert at new position
    testimonials.splice(newIndex, 0, movedTestimonial);

    updateCustomization(prev => ({
      ...prev,
      testimonials: testimonials
    }));
  };

  // CSV Utility Functions
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    // Detect delimiter - check if line uses semicolons or commas
    const hasSemicolon = line.includes(';');
    const hasComma = line.includes(',');
    const delimiter = (hasSemicolon && !hasComma) ? ';' : ',';
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    
    // Filter out empty trailing fields (common with semicolon-delimited CSVs)
    while (result.length > 0 && result[result.length - 1] === '') {
      result.pop();
    }
    
    return result;
  };

  const escapeCSVField = (field: string): string => {
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  // Export Functions
  const exportProcessSteps = () => {
    try {
      const headers = ['id', 'number', 'title', 'description'];
      const rows = customization.processSteps.map(step => [
        step.id,
        step.number.toString(),
        escapeCSVField(step.title),
        escapeCSVField(step.description)
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `process-steps-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(`Failed to export process steps: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const exportExpectations = () => {
    try {
      const headers = ['id', 'icon', 'title', 'description'];
      const rows = customization.expectations.map(exp => [
        exp.id,
        escapeCSVField(exp.icon),
        escapeCSVField(exp.title),
        escapeCSVField(exp.description)
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expectations-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(`Failed to export expectations: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const exportTestimonials = () => {
    try {
      const headers = ['id', 'content', 'author', 'service'];
      const rows = customization.testimonials.map(test => [
        test.id,
        escapeCSVField(test.content),
        escapeCSVField(test.author),
        escapeCSVField(test.service)
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `testimonials-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(`Failed to export testimonials: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Import Functions
  const handleImportProcessSteps = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row.');
      }

      // Parse headers first, then lowercase each one individually
      const rawHeaders = parseCSVLine(lines[0]);
      const headers = rawHeaders.map(h => h.trim().toLowerCase());
      const expectedHeaders = ['id', 'number', 'title', 'description'];
      
      // Validate headers
      const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        throw new Error(`Missing required columns: ${missingHeaders.join(', ')}. Found: ${headers.join(', ')}`);
      }

      const headerMap: { [key: string]: number } = {};
      headers.forEach((h, i) => {
        headerMap[h] = i;
      });

      const importedSteps: ProcessStep[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length < expectedHeaders.length) continue;

        const id = values[headerMap['id']]?.trim() || `step-${Date.now()}-${i}`;
        const number = parseInt(values[headerMap['number']]?.trim() || '0', 10) || i;
        const title = values[headerMap['title']]?.trim() || '';
        const description = values[headerMap['description']]?.trim() || '';

        if (title || description) {
          importedSteps.push({ id, number, title, description });
        }
      }

      if (importedSteps.length === 0) {
        throw new Error('No valid process steps found in the CSV file.');
      }

      // Auto-correct numbers based on order
      importedSteps.forEach((step, index) => {
        step.number = index + 1;
      });

      setImportDialog({
        isOpen: true,
        section: 'processSteps',
        data: importedSteps,
        onConfirm: (replace: boolean) => {
          console.log(`✅ Process steps import confirmed: replace=${replace}, items=${importedSteps.length}`);
          if (replace) {
            updateCustomization(prev => {
              const updated = { ...prev, processSteps: importedSteps };
              console.log('💾 Replacing process steps:', updated);
              setTimeout(() => saveCustomization(updated, true), 100);
              return updated;
            });
          } else {
            // Merge: append new items, avoiding duplicate IDs
            updateCustomization(prev => {
              const existingIds = new Set(prev.processSteps.map(s => s.id));
              const newSteps = importedSteps.filter(s => !existingIds.has(s.id));
              const nextNumber = prev.processSteps.length + 1;
              newSteps.forEach((step, index) => {
                step.number = nextNumber + index;
              });
              const updated = { ...prev, processSteps: [...prev.processSteps, ...newSteps] };
              console.log('💾 Merging process steps:', updated);
              setTimeout(() => saveCustomization(updated, true), 100);
              return updated;
            });
          }
          setImportDialog({ isOpen: false, section: null, data: [], onConfirm: () => {} });
          setErrorMessage(null);
        }
      });

      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      setErrorMessage(`Failed to import process steps: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleImportExpectations = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('❌ No file selected');
      return;
    }

    console.log('📥 Starting expectations import from file:', file.name);

    try {
      const text = await file.text();
      console.log('📄 File content length:', text.length);
      const lines = text.split('\n').filter(line => line.trim());
      console.log('📊 Total lines (after filtering):', lines.length);
      
      if (lines.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row.');
      }

      // Parse headers first, then lowercase each one individually
      const rawHeaders = parseCSVLine(lines[0]);
      console.log('📋 Raw headers:', rawHeaders);
      const headers = rawHeaders.map(h => h.trim().toLowerCase());
      console.log('📋 Normalized headers:', headers);
      const expectedHeaders = ['id', 'icon', 'title', 'description'];
      
      const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        throw new Error(`Missing required columns: ${missingHeaders.join(', ')}. Found: ${headers.join(', ')}`);
      }

      const headerMap: { [key: string]: number } = {};
      headers.forEach((h, i) => {
        headerMap[h] = i;
      });
      console.log('🗺️ Header map:', headerMap);

      const importedExpectations: Expectation[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0) {
          console.log(`⏭️ Skipping empty row ${i}`);
          continue;
        }

        const id = values[headerMap['id']]?.trim() || `exp-${Date.now()}-${i}`;
        const icon = values[headerMap['icon']]?.trim() || 'fas fa-star';
        const title = values[headerMap['title']]?.trim() || '';
        const description = values[headerMap['description']]?.trim() || '';

        console.log(`📝 Row ${i}: id="${id}", title="${title}", description="${description.substring(0, 50)}..."`);

        if (title || description) {
          importedExpectations.push({ id, icon, title, description });
        } else {
          console.log(`⚠️ Skipping row ${i} - no title or description`);
        }
      }

      console.log(`✅ Parsed ${importedExpectations.length} expectations:`, importedExpectations);

      if (importedExpectations.length === 0) {
        throw new Error('No valid expectations found in the CSV file. Make sure rows have at least a title or description.');
      }

      console.log('💬 Opening import dialog...');
      setImportDialog({
        isOpen: true,
        section: 'expectations',
        data: importedExpectations,
        onConfirm: (replace: boolean) => {
          console.log(`✅ Import confirmed: replace=${replace}, items=${importedExpectations.length}`);
          if (replace) {
            console.log('🔄 Replacing all expectations');
            updateCustomization(prev => {
              const updated = {
                ...prev,
                expectations: importedExpectations
              };
              console.log('💾 Updated customization:', updated);
              return updated;
            });
          } else {
            console.log('➕ Merging expectations');
            const existingIds = new Set(customization.expectations.map(e => e.id));
            const newExpectations = importedExpectations.filter(e => !existingIds.has(e.id));
            console.log(`📊 Existing: ${customization.expectations.length}, New: ${newExpectations.length}, Duplicates skipped: ${importedExpectations.length - newExpectations.length}`);
            updateCustomization(prev => {
              const updated = {
                ...prev,
                expectations: [...prev.expectations, ...newExpectations]
              };
              console.log('💾 Updated customization:', updated);
              return updated;
            });
          }
          setImportDialog({ isOpen: false, section: null, data: [], onConfirm: () => {} });
          setErrorMessage(null);
          console.log('✅ Import completed successfully');
        }
      });

      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      console.error('❌ Import error:', error);
      setErrorMessage(`Failed to import expectations: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleImportTestimonials = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row.');
      }

      // Parse headers first, then lowercase each one individually
      const rawHeaders = parseCSVLine(lines[0]);
      const headers = rawHeaders.map(h => h.trim().toLowerCase());
      const expectedHeaders = ['id', 'content', 'author', 'service'];
      
      const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        throw new Error(`Missing required columns: ${missingHeaders.join(', ')}. Found: ${headers.join(', ')}`);
      }

      const headerMap: { [key: string]: number } = {};
      headers.forEach((h, i) => {
        headerMap[h] = i;
      });

      const importedTestimonials: Testimonial[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length < expectedHeaders.length) continue;

        const id = values[headerMap['id']]?.trim() || `test-${Date.now()}-${i}`;
        const content = values[headerMap['content']]?.trim() || '';
        const author = values[headerMap['author']]?.trim() || '';
        const service = values[headerMap['service']]?.trim() || '';

        if (content || author) {
          importedTestimonials.push({ id, content, author, service });
        }
      }

      if (importedTestimonials.length === 0) {
        throw new Error('No valid testimonials found in the CSV file.');
      }

      setImportDialog({
        isOpen: true,
        section: 'testimonials',
        data: importedTestimonials,
        onConfirm: (replace: boolean) => {
          if (replace) {
            updateCustomization(prev => {
              const updated = { ...prev, testimonials: importedTestimonials };
              setTimeout(() => saveCustomization(updated, true), 100);
              return updated;
            });
          } else {
            updateCustomization(prev => {
              const existingIds = new Set(prev.testimonials.map(t => t.id));
              const newTestimonials = importedTestimonials.filter(t => !existingIds.has(t.id));
              const updated = { ...prev, testimonials: [...prev.testimonials, ...newTestimonials] };
              setTimeout(() => saveCustomization(updated, true), 100);
              return updated;
            });
          }
          setImportDialog({ isOpen: false, section: null, data: [], onConfirm: () => {} });
          setErrorMessage(null);
        }
      });

      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      setErrorMessage(`Failed to import testimonials: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  return (
    <section className="admin-section">
      <div className="admin-header">
        <h2>
          <i className="fas fa-cogs"></i>
          Package text
        </h2>
        <p>The notes, steps, and testimonials shown on every package.</p>
      </div>

      <div className="customization-container">
        {/* Global Booking Note */}
        <div className="customization-section">
          <div className="section-header">
            <h3>Global Booking Note</h3>
            <p>Default message shown in package modals</p>
          </div>
          <div className="form-field">
            <textarea
              value={customization.globalBookingNote}
              onChange={e => updateCustomization(prev => ({ ...prev, globalBookingNote: e.target.value }))}
              className="form-textarea"
              rows={3}
              placeholder="Enter the default booking note..."
            />
          </div>
        </div>

        {/* Process Steps */}
        <div className="customization-section process-steps-section">
          <div className="section-header">
            <div className="section-title-group">
              <div className="section-icon">
                <i className="fas fa-list-ol"></i>
              </div>
              <div>
                <h3>Process Steps</h3>
                <p>How the photography process works - drag to reorder</p>
              </div>
            </div>
            <div className="section-actions">
              <button className="btn btn-info btn-sm" onClick={exportProcessSteps} title="Export to CSV">
                <i className="fas fa-file-export"></i>
                Export
              </button>
              <label className="btn btn-success btn-sm import-btn" title="Import from CSV">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportProcessSteps}
                  style={{ display: 'none' }}
                />
                <i className="fas fa-file-import"></i>
                Import
              </label>
              <button className="btn btn-primary btn-sm" onClick={addProcessStep}>
                <i className="fas fa-plus"></i>
                Add Step
              </button>
            </div>
          </div>
          
          <div className="table-container">
            {customization.processSteps.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No process steps yet. Click "Add Step" to get started.</p>
              </div>
            ) : (
              <table className="customization-table">
                <thead>
                  <tr>
                    <th className="col-number">#</th>
                    <th className="col-title">Title</th>
                    <th className="col-description">Description</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customization.processSteps.map((step, index) => (
                    <tr key={step.id}>
                      <td className="col-number">
                        <input
                          type="number"
                          min="1"
                          max={customization.processSteps.length}
                          value={step.number}
                          onChange={e => {
                            const newNumber = parseInt(e.target.value, 10);
                            if (!isNaN(newNumber)) {
                              updateProcessStepOrder(step.id, newNumber);
                            }
                          }}
                          className="order-input"
                        />
                      </td>
                      <td className="col-title">
                        <input
                          type="text"
                          value={step.title}
                          onChange={e => updateProcessStep(step.id, 'title', e.target.value)}
                          placeholder="Step title"
                          className="table-input"
                        />
                      </td>
                      <td className="col-description">
                        <textarea
                          value={step.description}
                          onChange={e => updateProcessStep(step.id, 'description', e.target.value)}
                          placeholder="Step description"
                          className="table-textarea"
                          rows={2}
                        />
                      </td>
                      <td className="col-actions">
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this process step?')) {
                              removeProcessStep(step.id);
                            }
                          }}
                          title="Remove step"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Expectations */}
        <div className="customization-section expectations-section">
          <div className="section-header">
            <div className="section-title-group">
              <div className="section-icon">
                <i className="fas fa-star"></i>
              </div>
              <div>
                <h3>What to Expect</h3>
                <p>Client expectations and guarantees</p>
              </div>
            </div>
            <div className="section-actions">
              <button className="btn btn-info btn-sm" onClick={exportExpectations} title="Export to CSV">
                <i className="fas fa-file-export"></i>
                Export
              </button>
              <label className="btn btn-success btn-sm import-btn" title="Import from CSV">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportExpectations}
                  style={{ display: 'none' }}
                />
                <i className="fas fa-file-import"></i>
                Import
              </label>
              <button className="btn btn-primary btn-sm" onClick={addExpectation}>
                <i className="fas fa-plus"></i>
                Add Expectation
              </button>
            </div>
          </div>
          
          <div className="table-container">
            {customization.expectations.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No expectations yet. Click "Add Expectation" to get started.</p>
              </div>
            ) : (
              <table className="customization-table">
                <thead>
                  <tr>
                    <th className="col-number">#</th>
                    <th className="col-icon">Icon</th>
                    <th className="col-title">Title</th>
                    <th className="col-description">Description</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customization.expectations.map((exp, index) => (
                    <tr key={exp.id}>
                      <td className="col-number">
                        <input
                          type="number"
                          min="1"
                          max={customization.expectations.length}
                          value={index + 1}
                          onChange={e => {
                            const newOrder = parseInt(e.target.value, 10);
                            if (!isNaN(newOrder)) {
                              updateExpectationOrder(exp.id, newOrder);
                            }
                          }}
                          className="order-input"
                        />
                      </td>
                      <td className="col-icon">
                        <div className="icon-preview">
                          <i className={exp.icon}></i>
                        </div>
                        <input
                          type="text"
                          value={exp.icon}
                          onChange={e => updateExpectation(exp.id, 'icon', e.target.value)}
                          placeholder="fas fa-star"
                          className="table-input icon-input"
                        />
                      </td>
                      <td className="col-title">
                        <input
                          type="text"
                          value={exp.title}
                          onChange={e => updateExpectation(exp.id, 'title', e.target.value)}
                          placeholder="Expectation title"
                          className="table-input"
                        />
                      </td>
                      <td className="col-description">
                        <textarea
                          value={exp.description}
                          onChange={e => updateExpectation(exp.id, 'description', e.target.value)}
                          placeholder="Expectation description"
                          className="table-textarea"
                          rows={2}
                        />
                      </td>
                      <td className="col-actions">
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this expectation?')) {
                              removeExpectation(exp.id);
                            }
                          }}
                          title="Remove expectation"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Testimonials */}
        <div className="customization-section testimonials-section">
          <div className="section-header">
            <div className="section-title-group">
              <div className="section-icon">
                <i className="fas fa-quote-left"></i>
              </div>
              <div>
                <h3>Testimonials</h3>
                <p>Client reviews and feedback - drag to reorder</p>
              </div>
            </div>
            <div className="section-actions">
              <button className="btn btn-info btn-sm" onClick={exportTestimonials} title="Export to CSV">
                <i className="fas fa-file-export"></i>
                Export
              </button>
              <label className="btn btn-success btn-sm import-btn" title="Import from CSV">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportTestimonials}
                  style={{ display: 'none' }}
                />
                <i className="fas fa-file-import"></i>
                Import
              </label>
              <button className="btn btn-primary btn-sm" onClick={addTestimonial}>
                <i className="fas fa-plus"></i>
                Add Testimonial
              </button>
            </div>
          </div>
          
          <div className="table-container">
            {customization.testimonials.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No testimonials yet. Click "Add Testimonial" to get started.</p>
              </div>
            ) : (
              <table className="customization-table">
                <thead>
                  <tr>
                    <th className="col-number">#</th>
                    <th className="col-content">Content</th>
                    <th className="col-author">Author</th>
                    <th className="col-service">Service</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customization.testimonials.map((test, index) => (
                    <tr key={test.id}>
                      <td className="col-number">
                        <input
                          type="number"
                          min="1"
                          max={customization.testimonials.length}
                          value={index + 1}
                          onChange={e => {
                            const newOrder = parseInt(e.target.value, 10);
                            if (!isNaN(newOrder)) {
                              updateTestimonialOrder(test.id, newOrder);
                            }
                          }}
                          className="order-input"
                        />
                      </td>
                      <td className="col-content">
                        <textarea
                          value={test.content}
                          onChange={e => updateTestimonial(test.id, 'content', e.target.value)}
                          placeholder="Testimonial content"
                          className="table-textarea"
                          rows={3}
                        />
                      </td>
                      <td className="col-author">
                        <input
                          type="text"
                          value={test.author}
                          onChange={e => updateTestimonial(test.id, 'author', e.target.value)}
                          placeholder="Client name"
                          className="table-input"
                        />
                      </td>
                      <td className="col-service">
                        <input
                          type="text"
                          value={test.service}
                          onChange={e => updateTestimonial(test.id, 'service', e.target.value)}
                          placeholder="Service type"
                          className="table-input"
                        />
                      </td>
                      <td className="col-actions">
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this testimonial?')) {
                              removeTestimonial(test.id);
                            }
                          }}
                          title="Remove testimonial"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Save Status */}
        <div className="save-section">
          {saveStatus === 'saving' && (
            <div className="save-status saving">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Saving changes...</span>
            </div>
          )}
          {saveStatus === 'saved' && (
            <div className="save-status saved">
              <i className="fas fa-check-circle"></i>
              <span>Changes saved automatically</span>
            </div>
          )}
          {saveStatus === 'idle' && (
            <div className="save-status idle">
              <i className="fas fa-info-circle"></i>
              <span>Changes are saved automatically</span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{errorMessage}</span>
            <button onClick={() => setErrorMessage(null)} className="error-close">
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>

      {/* Import Confirmation Dialog */}
      {importDialog.isOpen && createPortal(
        <div className="import-dialog-overlay" onClick={() => setImportDialog({ isOpen: false, section: null, data: [], onConfirm: () => {} })}>
          <div className="import-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="import-dialog-header">
              <h3>Import {importDialog.section === 'processSteps' ? 'Process Steps' : importDialog.section === 'expectations' ? 'Expectations' : 'Testimonials'}</h3>
              <button 
                className="import-dialog-close"
                onClick={() => setImportDialog({ isOpen: false, section: null, data: [], onConfirm: () => {} })}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="import-dialog-content">
              <p>Found <strong>{importDialog.data.length}</strong> item(s) to import.</p>
              <p>How would you like to handle the import?</p>
              <div className="import-options">
                <button 
                  className="btn btn-danger"
                  onClick={() => importDialog.onConfirm(true)}
                >
                  <i className="fas fa-exchange-alt"></i>
                  Replace All Existing
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => importDialog.onConfirm(false)}
                >
                  <i className="fas fa-plus"></i>
                  Merge (Add New Only)
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setImportDialog({ isOpen: false, section: null, data: [], onConfirm: () => {} })}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default PackageCustomization;
